import { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';
import { useAppStore } from '@/store';
import type { GamutCurve, GrayBalanceDiagnostic, GrayBalanceStep } from '@/types';

// =============================================================================
// Layout constants
// =============================================================================

const M  = { top: 20, right: 24, bottom: 40, left: 44 };
const W  = 520, H = 340;
const IW = W - M.left - M.right;
const IH = H - M.top  - M.bottom;

// =============================================================================
// Collapsible Panel
// =============================================================================

function Panel({ title, children, defaultOpen = true }: {
  title: string; children: React.ReactNode; defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="rounded-xl border border-slate-700 bg-slate-800/50 overflow-hidden">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-4 py-3
                   text-sm font-medium text-slate-200 hover:bg-slate-700/30 transition-colors"
      >
        <span>{title}</span>
        <span className="text-slate-500 text-xs">{open ? '▲' : '▼'}</span>
      </button>
      {open && <div className="px-4 pb-4">{children}</div>}
    </div>
  );
}

// =============================================================================
// Chart 2a — CMY Primaries on a*b* plane
// =============================================================================

// Colors from spec §03 table
const CURVE_COLORS: Record<string, string> = {
  C:   '#06b6d4',  // cyan     (sky-400)
  M:   '#ec4899',  // magenta  (pink-400)
  Y:   '#eab308',  // yellow   (yellow-400)
  CM:  '#6366f1',  // indigo   (indigo-400)
  MY:  '#22c55e',  // green    (green-400)
  CY:  '#ef4444',  // red      (red-400)
  CMY: '#94a3b8',  // slate    (slate-400)
};

type CurveMap = Record<string, GamutCurve>;

function GamutChart({
  curves, paperWhite, neutralAB,
}: {
  curves:     CurveMap;
  paperWhite: { a: number; b: number };
  neutralAB?: { a: number; b: number }[];
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const svgRef       = useRef<SVGSVGElement>(null);
  const tipRef       = useRef<HTMLDivElement>(null);

  const [visible, setVisible] = useState<Record<string, boolean>>(
    Object.fromEntries(Object.keys(CURVE_COLORS).map(k => [k, true])),
  );

  useEffect(() => {
    if (!svgRef.current) return;
    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();
    const g = svg.append('g').attr('transform', `translate(${M.left},${M.top})`);

    // Gather all a*/b* values for auto-scale
    const allA: number[] = [], allB: number[] = [];
    for (const [label, curve] of Object.entries(curves)) {
      if (!visible[label]) continue;
      for (const pt of curve.points) { allA.push(pt.a); allB.push(pt.b); }
    }
    allA.push(paperWhite.a, 0); allB.push(paperWhite.b, 0);

    const pad = 10;
    const xDom = [Math.min(...allA) - pad, Math.max(...allA) + pad];
    const yDom = [Math.min(...allB) - pad, Math.max(...allB) + pad];
    const xScale = d3.scaleLinear().domain(xDom).nice().range([0, IW]);
    const yScale = d3.scaleLinear().domain(yDom).nice().range([IH, 0]);

    // ΔE (C*) reference rings at C*=1, 2, 5 — centered at neutral origin (0,0)
    for (const r of [1, 2, 5]) {
      const cx0 = xScale(0), cy0 = yScale(0);
      const rx  = Math.abs(xScale(r) - xScale(0));
      const ry  = Math.abs(yScale(0) - yScale(r));
      g.append('ellipse')
        .attr('cx', cx0).attr('cy', cy0)
        .attr('rx', rx).attr('ry', ry)
        .attr('fill', 'none')
        .attr('stroke', '#334155')
        .attr('stroke-dasharray', r === 1 ? '2,4' : '3,4')
        .attr('stroke-width', 0.8)
        .attr('opacity', 0.8);
      g.append('text')
        .attr('x', cx0 + rx + 2).attr('y', cy0 - 2)
        .attr('fill', '#475569').attr('font-size', 8)
        .text(`C*${r}`);
    }

    // Zero-axis cross lines
    g.append('line').attr('x1', xScale(0)).attr('x2', xScale(0))
      .attr('y1', 0).attr('y2', IH).attr('stroke', '#334155').attr('stroke-dasharray', '3,3');
    g.append('line').attr('x1', 0).attr('x2', IW)
      .attr('y1', yScale(0)).attr('y2', yScale(0)).attr('stroke', '#334155').attr('stroke-dasharray', '3,3');

    // Axes
    g.append('g').attr('transform', `translate(0,${IH})`)
      .call(d3.axisBottom(xScale).ticks(6))
      .selectAll('text,line,path').attr('stroke', '#64748b').attr('fill', '#64748b');
    g.append('g').call(d3.axisLeft(yScale).ticks(5))
      .selectAll('text,line,path').attr('stroke', '#64748b').attr('fill', '#64748b');
    g.append('text').attr('x', IW / 2).attr('y', IH + 32)
      .attr('text-anchor', 'middle').attr('fill', '#64748b').attr('font-size', 10).text('a*');
    g.append('text').attr('transform', 'rotate(-90)').attr('x', -IH / 2).attr('y', -32)
      .attr('text-anchor', 'middle').attr('fill', '#64748b').attr('font-size', 10).text('b*');

    // N-group neutral axis — small grey dots
    if (neutralAB?.length) {
      g.selectAll('circle.neutral')
        .data(neutralAB).enter().append('circle')
        .attr('class', 'neutral')
        .attr('cx', d => xScale(d.a))
        .attr('cy', d => yScale(d.b))
        .attr('r', 2)
        .attr('fill', '#475569')
        .attr('opacity', 0.7);
    }

    // Paper white × marker
    g.append('text')
      .attr('x', xScale(paperWhite.a)).attr('y', yScale(paperWhite.b))
      .attr('text-anchor', 'middle').attr('dominant-baseline', 'middle')
      .attr('fill', '#94a3b8').attr('font-size', 12).text('×');

    // Tooltip helpers (D3-managed, so no React state needed)
    const tip = d3.select(tipRef.current!);

    // Curves
    const lineGen = d3.line<{ a: number; b: number }>()
      .x(d => xScale(d.a)).y(d => yScale(d.b)).curve(d3.curveCatmullRom);

    for (const [label, curve] of Object.entries(curves)) {
      if (!visible[label] || !curve.points.length) continue;
      const color  = CURVE_COLORS[label] ?? '#94a3b8';
      const sorted = [...curve.points].sort((a, b) => a.ink - b.ink);

      // Polyline
      g.append('path').datum(sorted)
        .attr('fill', 'none').attr('stroke', color).attr('stroke-width', 1.8).attr('opacity', 0.8)
        .attr('d', lineGen as d3.ValueFn<SVGPathElement, { a: number; b: number }[], string | null>);

      // Dots — radius encodes normalised ink level
      const inkScale = curve.maxInk > 0 ? 1 / curve.maxInk : 1;
      g.selectAll(`circle.curve-${label}`)
        .data(sorted).enter().append('circle')
        .attr('class', `curve-${label}`)
        .attr('cx', d => xScale(d.a)).attr('cy', d => yScale(d.b))
        .attr('r', d => 2 + d.ink * inkScale * 4)
        .attr('fill', color).attr('fill-opacity', 0.7)
        .attr('stroke', d => (d.flags.hueAnomaly || d.flags.chromaAnomaly) ? '#ef4444' : 'none')
        .attr('stroke-width', 2)
        .attr('cursor', 'pointer')
        .on('mouseover', (event: MouseEvent, d) => {
          const container = containerRef.current;
          if (!container) return;
          const rect = container.getBoundingClientRect();
          tip.style('display', 'block')
            .html(
              `<b>${d.patchId}</b> &nbsp;<span style="color:${color}">${label}</span><br/>` +
              `ink: ${d.ink.toFixed(2)} &nbsp; a*: ${d.a.toFixed(1)} &nbsp; b*: ${d.b.toFixed(1)}<br/>` +
              `C*: ${d.chroma.toFixed(1)} &nbsp; h*: ${d.hueAngle.toFixed(0)}°` +
              (d.flags.hueAnomaly    ? ' &nbsp;<span style="color:#ef4444">⚠ hue</span>'    : '') +
              (d.flags.chromaAnomaly ? ' &nbsp;<span style="color:#ef4444">⚠ chroma</span>' : ''),
            )
            .style('left', (event.clientX - rect.left + 14) + 'px')
            .style('top',  (event.clientY - rect.top  - 40) + 'px');
        })
        .on('mouseout', () => tip.style('display', 'none'));
    }
  }, [curves, visible, paperWhite, neutralAB]);

  // Only show toggles for curves that have data
  const labels = Object.keys(CURVE_COLORS).filter(k => (curves[k]?.points.length ?? 0) > 0);

  return (
    <div ref={containerRef} className="relative">
      <svg ref={svgRef} viewBox={`0 0 ${W} ${H}`} className="w-full" />
      <div
        ref={tipRef}
        className="absolute bg-slate-800/95 border border-slate-600 rounded px-2 py-1.5
                   text-xs text-slate-200 pointer-events-none whitespace-nowrap"
        style={{ display: 'none', zIndex: 10 }}
      />
      <div className="flex flex-wrap gap-2 mt-2 justify-center">
        {labels.map(label => (
          <button
            key={label}
            onClick={() => setVisible(v => ({ ...v, [label]: !v[label] }))}
            className="px-2 py-0.5 rounded text-xs font-mono transition-opacity"
            style={{
              background: visible[label] ? CURVE_COLORS[label] + '33' : '#1e293b',
              color:      visible[label] ? CURVE_COLORS[label] : '#475569',
              border:     `1px solid ${visible[label] ? CURVE_COLORS[label] : '#334155'}`,
            }}
          >
            {label}
          </button>
        ))}
      </div>
    </div>
  );
}

// =============================================================================
// Chart 2b — Classic CMY Gray Balance (ΔC% / ΔY% vs M ink%)
// =============================================================================

function GrayBalanceChart({ report }: { report: GrayBalanceDiagnostic }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const svgRef       = useRef<SVGSVGElement>(null);
  const tipRef       = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!svgRef.current || !report.steps.length) return;
    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    // X: M ink % ascending (0 = white, 100 = full ink)
    const steps = [...report.steps].sort((a, b) => a.mInk - b.mInk);
    const g = svg.append('g').attr('transform', `translate(${M.left},${M.top})`);

    const xMax = Math.max(d3.max(steps.map(s => s.mInk * 100)) ?? 100, 50);
    const xScale = d3.scaleLinear().domain([0, xMax]).nice().range([0, IW]);

    // Y: ΔC% and ΔY% ink deviation
    const validC = steps.filter(s => s.dCInk !== null);
    const validY = steps.filter(s => s.dYInk !== null);
    const allPct = [
      ...validC.map(s => (s.dCInk as number) * 100),
      ...validY.map(s => (s.dYInk as number) * 100),
      0,
    ];
    const rawMin = Math.min(...allPct);
    const rawMax = Math.max(...allPct);
    const pad    = Math.max(Math.abs(rawMin), Math.abs(rawMax), 2) * 0.15;
    const yScale = d3.scaleLinear().domain([rawMin - pad, rawMax + pad]).nice().range([IH, 0]);

    // Axes
    g.append('g').attr('transform', `translate(0,${IH})`)
      .call(d3.axisBottom(xScale).ticks(6).tickFormat(d => `${+d}%`))
      .selectAll('text,line,path').attr('stroke', '#64748b').attr('fill', '#64748b');
    g.append('g').call(d3.axisLeft(yScale).ticks(6).tickFormat(d => `${(+d).toFixed(1)}%`))
      .selectAll('text,line,path').attr('stroke', '#64748b').attr('fill', '#64748b');

    // Axis labels
    g.append('text').attr('x', IW / 2).attr('y', IH + 32)
      .attr('text-anchor', 'middle').attr('fill', '#64748b').attr('font-size', 10)
      .text('M ink %  (0 = white)');
    g.append('text').attr('transform', 'rotate(-90)').attr('x', -IH / 2).attr('y', -36)
      .attr('text-anchor', 'middle').attr('fill', '#64748b').attr('font-size', 10)
      .text('ΔC / ΔY ink deviation (%)');

    // Zero reference
    g.append('line').attr('x1', 0).attr('x2', IW)
      .attr('y1', yScale(0)).attr('y2', yScale(0))
      .attr('stroke', '#475569').attr('stroke-dasharray', '4,3');

    // ±2% tolerance band (light fill)
    const yPos2 = yScale(2), yNeg2 = yScale(-2);
    if (yPos2 < yNeg2) {
      g.append('rect')
        .attr('x', 0).attr('y', yPos2)
        .attr('width', IW).attr('height', yNeg2 - yPos2)
        .attr('fill', '#22c55e08');
    }

    const tip = d3.select(tipRef.current!);
    const onOver = (event: MouseEvent, d: GrayBalanceStep) => {
      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect) return;
      tip.style('display', 'block')
        .html(
          `M=${(d.mInk * 100).toFixed(1)}%  G=${d.gRgb}  L*=${d.L.toFixed(1)}<br/>` +
          `ΔC: ${d.dCInk !== null ? (d.dCInk * 100).toFixed(2) + '%' : '—'}` +
          `&nbsp;&nbsp;ΔY: ${d.dYInk !== null ? (d.dYInk * 100).toFixed(2) + '%' : '—'}<br/>` +
          `ΔE cast: ${d.deltaE.toFixed(2)}`,
        )
        .style('left', (event.clientX - rect.left + 14) + 'px')
        .style('top',  (event.clientY - rect.top  - 44) + 'px');
    };
    const onOut = () => tip.style('display', 'none');

    // ΔC line — cyan
    const cLine = d3.line<GrayBalanceStep>()
      .x(d => xScale(d.mInk * 100))
      .y(d => yScale((d.dCInk as number) * 100))
      .curve(d3.curveMonotoneX);
    if (validC.length > 1) {
      g.append('path').datum(validC)
        .attr('fill', 'none').attr('stroke', '#06b6d4').attr('stroke-width', 2)
        .attr('d', cLine as d3.ValueFn<SVGPathElement, GrayBalanceStep[], string | null>);
      g.selectAll('circle.dc').data(validC).enter().append('circle')
        .attr('class', 'dc')
        .attr('cx', d => xScale(d.mInk * 100))
        .attr('cy', d => yScale((d.dCInk as number) * 100))
        .attr('r', 3.5).attr('fill', '#06b6d4').attr('stroke', '#1e293b').attr('stroke-width', 0.8)
        .attr('cursor', 'pointer').on('mouseover', onOver).on('mouseout', onOut);
    }

    // ΔY line — magenta
    const yLine = d3.line<GrayBalanceStep>()
      .x(d => xScale(d.mInk * 100))
      .y(d => yScale((d.dYInk as number) * 100))
      .curve(d3.curveMonotoneX);
    if (validY.length > 1) {
      g.append('path').datum(validY)
        .attr('fill', 'none').attr('stroke', '#ec4899').attr('stroke-width', 2)
        .attr('d', yLine as d3.ValueFn<SVGPathElement, GrayBalanceStep[], string | null>);
      g.selectAll('circle.dy').data(validY).enter().append('circle')
        .attr('class', 'dy')
        .attr('cx', d => xScale(d.mInk * 100))
        .attr('cy', d => yScale((d.dYInk as number) * 100))
        .attr('r', 3.5).attr('fill', '#ec4899').attr('stroke', '#1e293b').attr('stroke-width', 0.8)
        .attr('cursor', 'pointer').on('mouseover', onOver).on('mouseout', onOut);
    }

    // Legend
    const leg = g.append('g').attr('transform', `translate(4,4)`);
    ([['#06b6d4', 'ΔC ink'], ['#ec4899', 'ΔY ink']] as [string, string][])
      .forEach(([color, label], i) => {
        leg.append('rect').attr('x', 0).attr('y', i * 14).attr('width', 14).attr('height', 3)
          .attr('fill', color).attr('rx', 1);
        leg.append('text').attr('x', 18).attr('y', i * 14 + 5)
          .attr('fill', '#94a3b8').attr('font-size', 9).text(label);
      });
  }, [report]);

  return (
    <div ref={containerRef} className="relative">
      <svg ref={svgRef} viewBox={`0 0 ${W} ${H}`} className="w-full" />
      <div
        ref={tipRef}
        className="absolute bg-slate-800/95 border border-slate-600 rounded px-2 py-1.5
                   text-xs text-slate-200 pointer-events-none whitespace-nowrap"
        style={{ display: 'none', zIndex: 10 }}
      />
    </div>
  );
}

// =============================================================================
// Summary bar
// =============================================================================

function SummaryBar() {
  const diagnostics = useAppStore(s => s.session.diagnostics);
  const setView     = useAppStore(s => s.setView);

  if (!diagnostics) return null;

  const maxCast   = diagnostics.grayBalance.maxCast.toFixed(2);
  const castColor = diagnostics.grayBalance.maxCast > 2 ? 'text-red-400'
                  : diagnostics.grayBalance.maxCast > 1 ? 'text-amber-400'
                  : 'text-emerald-400';
  const cast  = diagnostics.grayBalance.dominantCast;
  const flagN = diagnostics.flaggedPatchIds.length;

  return (
    <div className="rounded-xl border border-slate-700 bg-slate-800/60 px-4 py-3
                    flex flex-wrap items-center gap-x-5 gap-y-2 text-sm">
      <span className={`${castColor} font-mono`}>
        Cast: {cast} &middot; max ΔE {maxCast}
      </span>
      {flagN > 0 && (
        <span className="text-amber-400 font-mono">
          ⚠ {flagN} anomalous patch{flagN !== 1 ? 'es' : ''}
        </span>
      )}
      <div className="ml-auto">
        <button
          onClick={() => setView('domain')}
          className="px-4 py-1.5 rounded-lg bg-sky-500 hover:bg-sky-400
                     text-slate-900 font-semibold text-sm transition-colors"
        >
          → Set Domain Ω
        </button>
      </div>
    </div>
  );
}

// =============================================================================
// DiagnoseView
// =============================================================================

export default function DiagnoseView() {
  const diagnostics = useAppStore(s => s.session.diagnostics);
  const patches     = useAppStore(s => s.session.patches);

  if (!diagnostics || !patches) {
    return (
      <div className="flex items-center justify-center h-full text-slate-500 font-mono text-sm">
        No diagnostic data — import a file first.
      </div>
    );
  }

  // Extract N-group a*,b* for the neutral axis overlay in the gamut chart
  const neutralAB = patches
    .filter(p => p.group === 'N')
    .map(p => ({ a: p.lab[1], b: p.lab[2] }));

  return (
    <div className="max-w-3xl mx-auto px-6 py-6 space-y-4">
      <div>
        <h1 className="text-lg font-semibold text-slate-100">Diagnostics</h1>
        <p className="text-sm text-slate-400 mt-0.5">
          {patches.length} patches ·{' '}
          paper white a*={diagnostics.paperWhite.a.toFixed(2)}{' '}
          b*={diagnostics.paperWhite.b.toFixed(2)}{' '}
          &middot; {diagnostics.flaggedPatchIds.length} flagged
        </p>
      </div>

      <Panel title="2a · CMY Gamut — a* vs b*">
        <GamutChart
          curves={diagnostics.gamut.curves as Record<string, GamutCurve>}
          paperWhite={{ a: diagnostics.paperWhite.a, b: diagnostics.paperWhite.b }}
          neutralAB={neutralAB}
        />
      </Panel>

      <Panel title="2b · Gray Balance Prediction">
        {diagnostics.grayBalance.steps.length > 0
          ? <GrayBalanceChart report={diagnostics.grayBalance} />
          : <p className="text-slate-500 text-sm py-2">No neutral (N-group) patches found.</p>}
      </Panel>

      <SummaryBar />
    </div>
  );
}
